import React, { useEffect, useState } from 'react';
import { Container, Typography, Grid, Paper, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput, TextField, Button, FormHelperText, CircularProgress } from '@mui/material';
import { Doughnut } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { useSelector } from 'react-redux';
import { getAuthHeaders } from '../../services/AuthService';
import { get } from '../../utils/ApiUtils';
import { LS_DELIVERY_PERFORMANCE_STATS_PATH } from '../../utils/Constants';
import { getEnv } from '../../utils/env';
import { ProtectedRoutes } from "../../common/ProtectedRoute";
import MenuDrawer from "../Dashboard/components/MenuDrawer";
import { getUserRoles } from "../../store/reducers/role";
import { useDbData } from '../Dashboard/hooks/useDbData';
import { Navigate } from 'react-router-dom';
import { enqueueSnackbar } from 'notistack';
import { getRegionCountries } from '../../utils/CountryUtils';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

const styles = {
  containerStyle: {
    marginTop: '2%',
    marginBottom: '2%'
  },
  paperStyle: {
    padding: '20px',
    textAlign: 'center',
    backgroundColor: '#f5f5f5'
  }
};

const Performance = ({allowedRoles, ...props}) => {
  const { distinctSearchFilters } = useDbData(props);
  const [view, setView] = React.useState('Dashboard');
  const [drawerState, setDrawerState] = React.useState('closed');
  const [deliveryStatusData, setDeliveryStatusData] = useState([]);
  const [visibleStages, setVisibleStages] = useState(['Success', 'Failed']);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCarriers, setSelectedCarriers] = useState([]);
  const [selectedSources, setSelectedSources] = useState([]);
  const [selectedDeliveryTypes, setSelectedDeliveryTypes] = useState([]);
  const [selectedClaimTypes, setSelectedClaimTypes] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [countryError, setCountryError] = useState(false);
  const [fromDateError, setFromDateError] = useState(false);
  const [toDateError, setToDateError] = useState(false);
  const [loading, setLoading] = useState(false);
  const _user = useSelector((state) => state.user);
  const userRoles = useSelector(getUserRoles);

  const regionCountries = getRegionCountries(userRoles);



  const fetchDeliveryStatusStats = async () => {
    if (!selectedCountry) return;

    setLoading(true);
    const accessToken = _user.accessToken;
    const authHeaders = getAuthHeaders(accessToken);
    const carriersParam = selectedCarriers.length > 0 ? `&deliveryPartners=${selectedCarriers.join(',')}` : '';
    const sourcesParam = selectedSources.length > 0 ? `&sources=${selectedSources.join(',')}` : '';
    const deliveryTypesParam = selectedDeliveryTypes.length > 0 ? `&deliveryTypes=${selectedDeliveryTypes.join(',')}` : '';
    const claimTypesParam = selectedClaimTypes.length > 0 ? `&claimTypes=${selectedClaimTypes.join(',')}` : '';
    const fromDateParam = fromDate ? `&fromDate=${Math.floor(new Date(fromDate).getTime() / 1000)}` : '';
    const toDateParam = toDate ? `&toDate=${Math.floor(new Date(toDate).getTime() / 1000)}` : '';
    const response = await get(
      getEnv('REACT_APP_SERVER_URL') + LS_DELIVERY_PERFORMANCE_STATS_PATH + `?country=${selectedCountry}${carriersParam}${sourcesParam}${deliveryTypesParam}${claimTypesParam}${fromDateParam}${toDateParam}`,
      { "Content-Type": "application/json", ...authHeaders }
    );

    if (response.code === 2000) {
      setDeliveryStatusData(response.result);
    }
    setLoading(false);
  };

  const handleCountryChange = (event) => {
    setSelectedCountry(event.target.value);
    setCountryError(false);
  };

  const handleCarrierChange = (event) => {
    setSelectedCarriers(event.target.value);
  };

  const handleFromDateChange = (event) => {
    setFromDate(event.target.value);
    setFromDateError(false);
  };

  const handleToDateChange = (event) => {
    setToDate(event.target.value);
    setToDateError(false);
  };

  const handleSourceChange = (event) => {
    setSelectedSources(event.target.value);
  };

  const handleDeliveryTypeChange = (event) => {
    setSelectedDeliveryTypes(event.target.value);
  };

  const handleClaimTypeChange = (event) => {
    setSelectedClaimTypes(event.target.value);
  };

  const handleSubmit = () => {
    let hasError = false;
    if (!selectedCountry) {
      setCountryError(true);
      hasError = true;
    }
    if (!fromDate) {
      setFromDateError(true);
      hasError = true;
    }
    if (!toDate) {
      setToDateError(true);
      hasError = true;
    }
    if (hasError) return;
    if (_user.accessToken) {
      fetchDeliveryStatusStats();
    }
  };

  const doughnutOptions = {
    maintainAspectRatio: false,
    plugins: {
      title: {
        display: true,
        text: 'Delivery Success vs. Failure',
        font: {
          size: 16
        }
      },
      legend: {
        onClick: (e, legendItem) => {
          const stage = legendItem.text;
          if (['In Progress', 'Exception'].includes(stage)) {
            if (visibleStages.includes(stage)) {
              setVisibleStages(visibleStages.filter(s => s !== stage));
            } else {
              setVisibleStages([...visibleStages, stage]);
            }
          }
        }
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed} (${percentage}%)`;
          }
        }
      }
    }
  };

  const filteredData = deliveryStatusData.filter(item => visibleStages.includes(item.stage));

  const doughnutChartData = {
    labels: filteredData.map(item => item.stage),
    datasets: [
      {
        data: filteredData.map(item => item.count),
        backgroundColor: filteredData.map(item => item.bgcolor),
        borderWidth: 1,
      },
    ],
  };

  const onViewSelected = (view) => {
    setView(view);
  };

  const onDrawerOpen = () => {
    setDrawerState('open');
  };

  const onDrawerClose = () => {
    setDrawerState('closed');
  };

  const hasPermission = userRoles.some(role => allowedRoles.includes(role));
  if (!hasPermission) {
    enqueueSnackbar('Access Denied', { autoHideDuration: 2000, variant: 'error' });
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <ProtectedRoutes>
      <MenuDrawer onDrawerOpen={onDrawerOpen} onDrawerClose={onDrawerClose} onViewSelected={onViewSelected}>
        <Container component="main" maxWidth="xl" style={styles.containerStyle}>
          <Typography variant="h4" component="h1" gutterBottom>
            Performance
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper style={styles.paperStyle}>
                <div style={{ textAlign: 'left', marginBottom: '10px' }}>
                  <div style={{ display: 'flex', gap: '16px', marginBottom: '15px' }}>
                    <FormControl sx={{ flex: 1 }} size="small" error={countryError}>
                      <InputLabel>Country *</InputLabel>
                      <Select
                        value={selectedCountry}
                        onChange={handleCountryChange}
                        label="Country *"
                        displayEmpty
                        size="small"
                        error={countryError}
                      >
                        {regionCountries.sort((a, b) => a.name.localeCompare(b.name)).map((country) => (
                          <MenuItem key={country.value} value={country.value}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      {countryError && <FormHelperText>Country is required</FormHelperText>}
                    </FormControl>
                    <TextField
                      label="From Date *"
                      type="date"
                      value={fromDate}
                      onChange={handleFromDateChange}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                      size="small"
                      error={fromDateError}
                      helperText={fromDateError ? "From Date is required" : ""}
                    />
                    <TextField
                      label="To Date *"
                      type="date"
                      value={toDate}
                      onChange={handleToDateChange}
                      InputLabelProps={{ shrink: true }}
                      sx={{ flex: 1 }}
                      size="small"
                      error={toDateError}
                      helperText={toDateError ? "To Date is required" : ""}
                    />
                    <FormControl sx={{ flex: 1 }} size="small">
                      <InputLabel>Carriers</InputLabel>
                      <Select
                        multiple
                        value={selectedCarriers}
                        onChange={handleCarrierChange}
                        input={<OutlinedInput label="Carriers" />}
                        renderValue={(selected) => selected.join(', ')}
                        size="small"
                      >
                        {distinctSearchFilters?.carriers?.sort((a, b) => a.value.localeCompare(b.value)).map((carrier) => (
                          <MenuItem key={carrier.id} value={carrier.name}>
                            <Checkbox checked={selectedCarriers.indexOf(carrier.name) > -1} />
                            <ListItemText primary={carrier.value} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <FormControl sx={{ flex: 1 }} size="small">
                      <InputLabel>Source</InputLabel>
                      <Select
                        multiple
                        value={selectedSources}
                        onChange={handleSourceChange}
                        input={<OutlinedInput label="Source" />}
                        renderValue={(selected) => selected.join(', ')}
                        size="small"
                      >
                        {distinctSearchFilters?.sources?.sort((a, b) => a.value.localeCompare(b.value)).map((source) => (
                          <MenuItem key={source.id} value={source.name}>
                            <Checkbox checked={selectedSources.indexOf(source.name) > -1} />
                            <ListItemText primary={source.value} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="small">
                      <InputLabel>Delivery Type</InputLabel>
                      <Select
                        multiple
                        value={selectedDeliveryTypes}
                        onChange={handleDeliveryTypeChange}
                        input={<OutlinedInput label="Delivery Type" />}
                        renderValue={(selected) => selected.join(', ')}
                        size="small"
                      >
                        {distinctSearchFilters?.deliveryTypes?.sort((a, b) => a.value.localeCompare(b.value)).map((deliveryType) => (
                          <MenuItem key={deliveryType.id} value={deliveryType.name}>
                            <Checkbox checked={selectedDeliveryTypes.indexOf(deliveryType.name) > -1} />
                            <ListItemText primary={deliveryType.value} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl sx={{ flex: 1 }} size="small">
                      <InputLabel>Product Type</InputLabel>
                      <Select
                        multiple
                        value={selectedClaimTypes}
                        onChange={handleClaimTypeChange}
                        input={<OutlinedInput label="Product Type" />}
                        renderValue={(selected) => selected.join(', ')}
                        size="small"
                      >
                        {distinctSearchFilters?.claimTypes?.sort((a, b) => a.value.localeCompare(b.value)).map((claimType) => (
                          <MenuItem key={claimType.id} value={claimType.name}>
                            <Checkbox checked={selectedClaimTypes.indexOf(claimType.name) > -1} />
                            <ListItemText primary={claimType.value} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="contained"
                      onClick={handleSubmit}
                      size='small'
                      sx={{ flex: 1, padding: 0 }}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
                    </Button>
                  </div>
                </div>
              </Paper>
            </Grid>
            {selectedCountry && deliveryStatusData.length > 0 && (
              <Grid item xs={12} md={6}>
                <Paper style={styles.paperStyle}>
                  <div style={{ width: '400px', height: '400px', margin: '0 auto' }}>
                    <Doughnut options={doughnutOptions} data={doughnutChartData} />
                  </div>
                  <div style={{ marginTop: '10px' }}>
                    {deliveryStatusData.filter(item => !visibleStages.includes(item.stage)).map(item => (
                      <span key={item.stage}
                        onClick={() => setVisibleStages([...visibleStages, item.stage])}
                        style={{ cursor: 'pointer', margin: '5px', padding: '5px', backgroundColor: item.bgcolor, color: 'white', borderRadius: '3px' }}>
                        {item.stage}
                      </span>
                    ))}
                  </div>
                </Paper>
              </Grid>
            )}
          </Grid>
        </Container>
      </MenuDrawer>
    </ProtectedRoutes>
  );
};

export default Performance;